import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users } from 'lucide-react';
import { Button, Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination, type FilterCondition, type FilterField } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useCustomerSegment, type CustomerSegment } from '@/api/customers';
import { CustomerListToolbar, colId, type SectionColumn } from './customer-list-toolbar';
import { paginationView } from '../pagination-summary';

const PAGE_SIZE = 20;

/** Debounce a fast-changing value (search box) to throttle server refetches. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export interface CustomerListSectionProps {
  segment: CustomerSegment;
  columns: SectionColumn[];
  filterFields?: FilterField[];
  searchPlaceholder?: string;
}

/**
 * Server-paginated customer segment list: Toolbar (search + Filters popover +
 * Display options) over a DataTable with row-click navigation and Pagination.
 * Search (`q`), filter conditions and page are pushed to the backend via
 * `useCustomerSegment`; loading → Skeleton, error → Alert+retry, empty →
 * EmptyState. Built FilterBuilder conditions collapse into the `filters` object
 * (e.g. `{ customer_industry: [...] }`) the API base64-encodes.
 */
export function CustomerListSection({
  segment,
  columns,
  filterFields,
  searchPlaceholder = 'Search…',
}: CustomerListSectionProps) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);

  const [visible, setVisible] = useState<Record<string, boolean>>(
    () => Object.fromEntries(columns.map((c) => [colId(c), true])),
  );

  // Applied conditions → the backend `filters` object (page is reset on change
  // by the search/apply handlers below).
  const filters = useMemo(() => conditionsToFilters(conditions), [conditions]);

  const { data, isLoading, isError, refetch, isFetching } = useCustomerSegment(segment, {
    page,
    page_size: PAGE_SIZE,
    q: query || undefined,
    filters: Object.keys(filters).length ? filters : undefined,
  });

  const rows = data?.results ?? [];
  const pagination = data?.pagination;
  const pageCount = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? rows.length;

  const visibleColumns = useMemo(
    () => columns.filter((c) => visible[colId(c)] !== false),
    [columns, visible],
  );

  const onSearch = (value: string) => {
    setRawQuery(value);
    setPage(1);
  };

  const applyFilters = (next: FilterCondition[]) => {
    setConditions(next.filter((c) => c.value !== ''));
    setPage(1);
    setFilterOpen(false);
  };


  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <CustomerListToolbar
        columns={columns}
        filterFields={filterFields}
        searchPlaceholder={searchPlaceholder}
        query={rawQuery}
        onQueryChange={onSearch}
        conditions={conditions}
        filterOpen={filterOpen}
        onFilterOpenChange={setFilterOpen}
        onApplyFilters={applyFilters}
        visible={visible}
        onToggleColumn={(id, checked) => setVisible((prev) => ({ ...prev, [id]: checked }))}
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Failed to load customers"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <Card flat>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </CardBody>
        </Card>
      ) : (
        <DataTable
          columns={visibleColumns}
          data={rows}
          getRowId={(r) => String(r.customer_id)}
          onRowClick={(r) => navigate(`/customers/${r.customer_id}`)}
          emptyState={
            <EmptyState
              icon={<Users size={24} />}
              title="No customers found"
              description="Try adjusting your search or filters."
            />
          }
          footer={(() => {
            const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
            return (
              <Pagination
                page={page}
                pageCount={view.pageCount}
                onPageChange={setPage}
                summary={`${view.summary}${isFetching ? ' …' : ''}`}
              />
            );
          })()}
        />
      )}
    </div>
  );
}

/**
 * Fold FilterBuilder conditions into the backend `filters` object. Multiselect
 * values arrive comma-joined; select/text collapse to a single-item array so the
 * backend's `IN`-style decode still applies. Multiple conditions on one field
 * merge their values.
 */
function conditionsToFilters(conditions: FilterCondition[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const c of conditions) {
    if (!c.value) continue;
    const values = c.value.split(',').map((s) => s.trim()).filter(Boolean);
    out[c.field] = [...(out[c.field] ?? []), ...values];
  }
  return out;
}
